import { h, FunctionComponent } from 'preact';
import { memo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as acceptFormActions from '~src/domains/accept/state/acceptFormReducer';
import InputGroup from '~src/domains/common/components/InputGroup';
import SecureInputGroup from '~src/domains/common/components/SecureInputGroup';
import { Page } from '~src/domains/common/enums/api';
import {
  AcceptFormNames,
  InputGroupWidth,
} from '~src/domains/common/enums/common';
import { Country } from '~src/domains/common/enums/localization';
import { setSecureForms } from '~src/domains/common/state/commonReducer';
import {
  CCHandoffIdSelector,
  CCHandoffUrlSelector,
  isOfferExpiredSelector,
  localeSelector,
} from '~src/domains/common/state/commonSelectors';
import * as styles from '~src/styles/domains/accept/AcceptPage.module.scss';
import { initSecureForms } from '~src/utils/secureForms';
import { acceptFormSelector } from '../state/acceptSelectors';

// Width styling on desktop
const getInputGroupClass = (width?: InputGroupWidth) => {
  return width === InputGroupWidth.THIRD_DESKTOP
    ? styles['accept-input-third-desktop']
    : '';
};

const inputGroup = (
  acceptForm: { [k: string]: any },
  inputs: acceptFormActions.AcceptLocalization,
  isSecure: boolean,
  key: string,
  width?: InputGroupWidth,
  extras?: string[],
) => {
  const state = acceptForm[key];
  const inputProp = inputs[key];
  if (!inputProp) {
    return;
  }

  const props = {
    state,
    extras,
    input: inputProp,
    id: key,
    page: Page.ACCEPT,
    className: '',
    reduxActions: acceptFormActions,
  };
  const input = isSecure ? (
    <SecureInputGroup {...props} />
  ) : (
    <InputGroup {...props} />
  );

  return <div className={getInputGroupClass(width)}>{input}</div>;
};

const CardForm: FunctionComponent<{
  inputs: acceptFormActions.AcceptLocalization;
  visible: boolean;
}> = memo(({ inputs, visible }) => {
  const dispatch = useDispatch();

  const isOfferExpired = useSelector(isOfferExpiredSelector);
  const locale = useSelector(localeSelector);
  const handoffId = useSelector(CCHandoffIdSelector);
  const handoffUrl = useSelector(CCHandoffUrlSelector);
  const acceptForm = useSelector(acceptFormSelector);

  const [_, country] = locale.split('-');
  const showSsnField =
    inputs[AcceptFormNames.FULL_SSN] && country === Country.US;

  useEffect(() => {
    if (handoffId && handoffUrl) {
      dispatch(initSecureForms(handoffId, handoffUrl, { country }));
    }

    if (!showSsnField) {
      dispatch(
        acceptFormActions.updateInputHidden({
          name: AcceptFormNames.FULL_SSN,
          value: true,
        }),
      );
    }

    return () => {
      dispatch(
        setSecureForms({
          initialized: false,
        }),
      );
    };
  }, []);

  return acceptForm && typeof inputs !== 'string' ? (
    <div
      className={
        visible ? styles['accept-inputs'] : styles['accept-inputs-hidden']
      }
    >
      {inputGroup(acceptForm, inputs, true, AcceptFormNames.CARD_INFO_NUMBER)}
      {inputGroup(
        acceptForm,
        inputs,
        true,
        AcceptFormNames.CARD_INFO_EXPIRATION,
        InputGroupWidth.THIRD_DESKTOP,
      )}
      {inputGroup(
        acceptForm,
        inputs,
        true,
        AcceptFormNames.CARD_INFO_CCV,
        InputGroupWidth.THIRD_DESKTOP,
      )}
      {inputGroup(
        acceptForm,
        inputs,
        true,
        AcceptFormNames.BILLING_ZIP,
        InputGroupWidth.THIRD_DESKTOP,
      )}
      {showSsnField &&
        !isOfferExpired &&
        inputGroup(acceptForm, inputs, false, AcceptFormNames.FULL_SSN)}
      {inputGroup(acceptForm, inputs, false, AcceptFormNames.CARD_INFO_NAME)}
    </div>
  ) : null;
});

export default CardForm;
